import React, { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from "uuid";
import classNames from 'classnames';
import {
    closestCorners,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    UniqueIdentifier,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    horizontalListSortingStrategy,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
    SortableContext,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandleIcon } from '@/assets/svgs/icons';


export const toSortableItem = <T,>(item: T): SortableItem<T> => {
    return {
        id: uuidv4(),
        data: item
    };
};

export const getUpdatedSortableList = <T,>(sortableArray: SortableItem<T>[], originalArray: T[]): SortableItem<T>[] => {
    return originalArray.map((item, index) =>
        sortableArray[index] ? { ...sortableArray[index], data: item } : toSortableItem(item)
    );
};

export type SortableItem<T> = {
    id: string;
    data: T;
}

interface SortableWrapperProps<T extends {id: UniqueIdentifier}> {
    label?: string;
    items: T[];
    direction?: 'vertical' | 'horizontal';
    disabled?: boolean;
    className?: string;
    onDragSortEnd: (items: T[]) => void;
    children: React.ReactNode;
}

export const SortableWrapper = <T extends {id: UniqueIdentifier}>(props: SortableWrapperProps<T>) => {
    const { children, items, onDragSortEnd, direction = 'vertical', disabled = false, className, label } = props;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        onDragSortEnd(arrayMove(items, oldIndex, newIndex));
    };


    return (
        <DndContext sensors={!disabled ? sensors : []}
                    collisionDetection={closestCorners}
                    onDragEnd={onDragEnd}
        >
            <SortableContext items={items}
                             strategy={
                                direction === 'vertical'
                                    ? verticalListSortingStrategy
                                    : horizontalListSortingStrategy
                            }
            >
                <ul className={className} aria-label={label}>
                    {children}
                </ul>
            </SortableContext>
        </DndContext>
    );
};

interface SortableItemWrapperBaseProps<T extends {id: UniqueIdentifier}> {
    item: T;
    className?: string;
    'aria-label'?: string;
}

type SortableItemWrapperProps<T extends {id: UniqueIdentifier}> =
    | (SortableItemWrapperBaseProps<T> & {
          dragTrigger?: "handle";
          children: (dragHandle: React.ReactNode) => React.ReactNode;
      })
    | (SortableItemWrapperBaseProps<T> & {
          dragTrigger: "full";
          children: React.ReactNode;
      });

export const SortableItemWrapper = <T extends {id: UniqueIdentifier}>(props: SortableItemWrapperProps<T>) => {
    const { item, className, 'aria-label': ariaLabel } = props;
    const isFullDragTrigger = props.dragTrigger === "full";

    const {
        mergeRef,
        transform,
        transition,
        isDragging,
        attributes,
        customListeners
    } = useSortableItem(item, isFullDragTrigger);

    if (isFullDragTrigger) {
        const { role: _dndRole, ...restAttrs } = attributes;
        return (
            <li ref={mergeRef}
                aria-label={ariaLabel}
                {...restAttrs}
                {...customListeners}
                style={{
                    transform: CSS.Translate.toString(transform),
                    transition,
                    opacity: isDragging ? 0.5 : undefined
                }}
                className={classNames("cursor-default active:cursor-grabbing", className)}
            >
                {props.children}
            </li>
        );
    }

    const DragHandle = (
        <div {...attributes}
             {...customListeners}
             data-testid="sortable-drag-handle"
             className="cursor-grab active:cursor-grabbing"
        >
            <DragHandleIcon className="size-5" />
        </div>
    );

    return (
        <li ref={mergeRef}
            aria-label={ariaLabel}
            style={{
                transform: CSS.Translate.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : undefined
            }}
            className={className}
        >
            {props.children(DragHandle)}
        </li>
    );
};

const useSortableItem = <T extends {id: UniqueIdentifier}>(item: T, preventDragOnInteractiveElements?: boolean) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: item.id, data: item });

    const internalRef = useRef<HTMLLIElement | null>(null);

    const mergeRef = useCallback((node: HTMLLIElement) => {
        setNodeRef(node);
        internalRef.current = node;
    }, [setNodeRef]);

    const customListeners = {
        ...listeners,
        onPointerDown: (e: React.PointerEvent) => {
            if (
                preventDragOnInteractiveElements
                && (e.target as HTMLElement).closest('button, input, textarea, select, label, a, [data-prevent-sortable="true"]')
            ) {
                return;
            }
            listeners?.onPointerDown?.(e);
        },
        onKeyDown: (e: React.KeyboardEvent) => {
            if (
                preventDragOnInteractiveElements && internalRef.current !== e.target
            ) {
                return;
            }
            listeners?.onKeyDown?.(e);
        },
    };

    return {
        mergeRef,
        transform,
        transition,
        isDragging,
        attributes,
        customListeners
    };
};

